import {BehaviorSubject, combineLatest, from, map, Observable} from 'rxjs';
import {Controller} from '../Controller';
import {DataSource} from '../../common/decorators/rxdb/DataSource';
import {tAsString} from '../../common/helpers/react/text/tAsString';
import {Bound} from '../../common/decorators/methods/Bound';
import {Throttled} from '../../common/decorators/methods/Throttled';
import {RxConfiguration} from '../../common/model/db/types/RxConfiguration';
import {DataSourceKind} from '../../common/decorators/rxdb/DataSourceKind';
import {asString} from '../../common/helpers/converters/asString';
import {ConfigurationResponse, SortOrder} from "../../core/rest-client/aat";
import {TableSortingState} from '../../common/model/types/sorting/TableSortingState';
import {sortByServerOrder} from '../../common/helpers/sorting/sortByServerOrder';
import {THROTTLING_INTERVAL_FOR_HTTP, TIMEOUT_FOR_INSTANT_ACTION} from "../../common/CONST";

export class ConfigurationsController extends Controller {
    #sortingState = new BehaviorSubject<TableSortingState>({
        sortBy: 'redappName',
        sortOrder: SortOrder.Asc
    });
    #order = new BehaviorSubject<string[] | undefined>(undefined);

    getSortingState$(): Observable<TableSortingState> {
        return this.#sortingState;
    }

    /**
     * Sorting is done by the server, so the new order is applied by refetching the list.
     * Throttling is bypassed on purpose - each requested order is a different query, so
     * a cached result of the previous one must not be reused.
     */
    async changeSorting(sortBy?: string, sortOrder?: SortOrder): Promise<void> {
        this.#sortingState.next({sortBy, sortOrder});

        await this.reloadConfigurations();
    }

    @Bound()
    @Throttled({
        recall: true,
        timeout: THROTTLING_INTERVAL_FOR_HTTP + TIMEOUT_FOR_INSTANT_ACTION
    })
    async refreshConfigurations(): Promise<void> {
        await this.reloadConfigurations();
    };

    private async reloadConfigurations(): Promise<void> {
        const {sortBy, sortOrder} = this.#sortingState.value;

        const order = await this.getBusyFlag().run(
            () => this.services.configurations.getConfigurations(sortBy, sortOrder),
            tAsString('REFRESHING_CONFIGURATIONS'));

        this.#order.next(order);
    }

    async createConfiguration(redappName: string, configurationName: string, value: string, description: string, forceSave?: boolean): Promise<ConfigurationResponse> {
        return await this.getBusyFlag().run(async () => {
            const response = await this.services.configurations.createConfiguration(redappName, configurationName, value, description, forceSave);

            await this.refreshConfigurations();
            return response
        }, tAsString('CREATING_CONFIGURATION'));
    };

    async getSchema(redappName: string): Promise<string> {
        return await this.getBusyFlag().run(async () => {
            return this.services.configurations.getSchema(redappName);
        }, tAsString('FETCHING_CONFIGURATION_SCHEMA'));
    }

    async updateConfiguration(configurationId: number, configurationName: string, value: string, description: string, forceSave?: boolean): Promise<ConfigurationResponse> {
        return await this.getBusyFlag().run(async () => {
            const response = await this.services.configurations.updateConfiguration(configurationId, configurationName, value, description, forceSave);

            await this.refreshConfigurations();
            return response
        }, tAsString('UPDATING_CONFIGURATION'));
    }

    @DataSource()
    getConfigurations$(): Observable<RxConfiguration[]> {
        return combineLatest([this.db.configurations.active$, this.#order]).pipe(
            map(([configurations, order]) => sortByServerOrder(configurations, order, it => it.id))
        );
    };

    @DataSource(DataSourceKind.PARAMETRIZED)
    getConfigurationById$(configurationId: number): Observable<RxConfiguration | null> {
        if (isNaN(configurationId)) {
            return from([null]);
        }

        return this.db.configurations.getById$(asString(configurationId));
    };

    async deleteConfigurationsByIds(listIds: number[]): Promise<void> {
        try {
            await this.getBusyFlag().run(async () => {
                await this.services.configurations.deleteConfigurationsByIds(listIds);
            }, tAsString('DELETING_CONFIGURATIONS'));
        } finally {
            this.refreshConfigurations().andWeAreDone();
        }
    }
}